import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { GridUtility } from '/imports/api/gridCapacity';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateString } from '/imports/validators';
import { GmapsAddress } from '/imports/gmaps';

export type ProspectType = 'address' | 'apn';

export interface ParcelData {
  apn: string;
  boundary?: GeoJSON.Feature | GeoJSON.FeatureCollection;
  centroid?: { lat: number; lng: number };
  county?: string;
  state?: string;
  ownerName?: string;
  landUse?: string;
  acreage?: number;
}

export interface Prospect extends Doc {
  orgId: string;
  prospectBatchId: string;
  name: string;
  type?: ProspectType;
  address?: GmapsAddress;
  parcel?: ParcelData;
  gmapsEnrichmentId?: string;
  reonomyEnrichmentId?: string;
  apnEnrichmentId?: string;
  sunroof?: { sqft: number, imagePath: string };
  solarApi?: { sqft: number, maxPanels?: number, maxKwDc?: number, existingSolar?: boolean, imageryQuality?: string, imageryDate?: string };
  nearmapEnrichmentId?: string;
  utilities?: string[];
  ccas?: string[];
  tariffEnrichmentId?: string;
  lsesEnrichmentId?: string;
  propertyTypeCombined?: string;
  annualConsumption?: number;
  maxDemand?: number;
  maxPvSize60?: number;   // kW DC sized to offset at most 60% of annualConsumption (computed at enrichment)
  maxPvSource?: string;   // rooftop source the nominal size came from (Nearmap/Sunroof/Google Footprints/Reonomy)
  /** what the grid and the incentive maps say about the site; written by the ICA enrichment */
  ica?: ProspectIca;
}

export interface ProspectIcaSubstation {
  lse: GridUtility;
  name?: string;
  distanceMiles: number;
  voltage?: string;
  /** MW */
  existingGeneration?: number;
  queuedGeneration?: number;
  totalGeneration?: number;
  /** % of projected load */
  penetration?: number;
  /** MW the substation could still take, read off its total generation and penetration */
  estimatedCapacity?: number;
}

export interface ProspectIcaCircuit {
  lse: GridUtility;
  circuit?: string;
  substation?: string;
  /** the nearest line section of the circuit, the one every figure below describes */
  section?: string;
  ohug?: string;
  voltage?: string;
  distanceMiles: number;
  /** MW on the whole circuit (SDG&E) */
  connectedGeneration?: number;
  queuedGeneration?: number;
  headroom?: number;
  /** MW the section can take: uniform generation with op flex, load without, PV with and without */
  icaUniformOpFlex?: number;
  icaLoad?: number;
  icaPvOpFlex?: number;
  icaPv?: number;
  /** the circuit's forecast monthly peak and trough, January first, in MW (SDG&E) */
  highLoad?: number[];
  lowLoad?: number[];
}

export interface ProspectIca {
  fetchedAt: Date;
  /** a source could not be read, so some of the figures below are missing */
  partial?: boolean;
  lmiPercent?: number;
  disadvantaged?: boolean;
  energyCommunity?: boolean;
  fossilFuelEmployment?: boolean;
  /** nearest first */
  substations: ProspectIcaSubstation[];
  circuits: ProspectIcaCircuit[];
}
export type Model = Prospect;

export const Prospects = new Mongo.Collection<Prospect>('prospects');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: Prospects,
  editableFields: ['active', 'orgId', 'prospectBatchId', 'name', 'type', 'address', 'parcel', 'gmapsEnrichmentId', 'reonomyEnrichmentId', 'apnEnrichmentId',
    'sunroof', 'solarApi', 'nearmapEnrichmentId', 'utilities', 'ccas', 'tariffEnrichmentId', 'lsesEnrichmentId', 'propertyTypeCombined', 'annualConsumption', 'maxDemand'],
  fieldLimits: {
    'apnEnrichmentId': 64, 'ccas': 128, 'gmapsEnrichmentId': 64, 'lsesEnrichmentId': 64, 'name': 512, 'nearmapEnrichmentId': 64,
    'orgId': 64, 'propertyTypeCombined': 256, 'prospectBatchId': 64, 'reonomyEnrichmentId': 64, 'tariffEnrichmentId': 64,
    'type': 64, 'utilities': 128,
  },
  isOrgOwned: true,
  listMethod: DeclareMethod<ListMethodArg<Prospect>, ListMethodResponse<Prospect, Refs>>('listProspects'),
  getMethod: DeclareMethod<string, GetMethodResponse<Prospect, Refs>>('getProspect'),
  createMethod: DeclareMethod<Partial<Prospect>, string>('createProspect'),
  updateMethod: DeclareMethod<Partial<Prospect>, string>('updateProspect'),
  deleteMethod: DeclareMethod<string>('deleteProspect'),
  pathPrefix: '/prospector/prospects',
  asString: (document) => document.name || document._id,
  modelName: 'Prospect',
  pluralName: 'Prospects',
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
        { 'parcel.apn': new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (prospect: Partial<Prospect>) => {
    const errors: { [key: string]: string[] } = {};
    validateString(errors, prospect, 'orgId', { required: true });
    validateString(errors, prospect, 'prospectBatchId', { required: true });
    return errors;
  },
};

export const addProspectToBatch = DeclareMethod<{ batchId: string, addressString: string, address?: GmapsAddress }, string>('addProspectToBatch');
export const enrichProspect = DeclareMethod<{ prospectId: string, sync?: boolean }>('enrichProspect');
export const enqueueProspectEnrichments = DeclareMethod<{ batchId: string, prospectIds: string[] }>('enqueueProspectEnrichments');
export const cancelProspectEnrichments = DeclareMethod<{ batchId: string }>('cancelProspectEnrichments');
export const enqueueIncompleteEnrichments = DeclareMethod<{ batchId: string }, number>('enqueueIncompleteEnrichments');
export const getProspectEnrichProgress = DeclareMethod<{ batchId: string }, { enrichingProspectId?: string, queueLength: number, total: number }>('getProspectEnrichProgress');
export const exportProspectsToXlsx = DeclareMethod<{ prospectIds: string[] }, string>('exportProspectsToXlsx');
export const getProspectFullDetails = DeclareMethod<{ prospectId: string }, { label: string, value: any }[]>('getProspectFullDetails');

Meteor.isServer && Meteor.startup(async () => {
  await Prospects.createIndexAsync({ orgId: 1 });
  await Prospects.createIndexAsync({ prospectBatchId: 1 });
  await Prospects.createIndexAsync({ prospectBatchId: 1, gmapsEnrichmentId: 1 }, { unique: true, sparse: true });
});
